import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { getDepartmentOptions } from "../../services/geoService";

const useDark = () => {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const check = () =>
      document.body.classList.contains("dark-mode") ||
      document.querySelector(".app")?.classList.contains("dark-mode");
    setIsDark(check());
    const target = document.querySelector(".app") || document.body;
    const obs = new MutationObserver(() => setIsDark(check()));
    obs.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
};

/**
 * Props:
 *  - countryId: string (requerido para cargar)
 *  - value: string (id seleccionado)
 *  - onChange: (id) => void
 *  - isDisabled?: boolean
 *  - placeholder?: string
 */
const SelectDepartment = ({
  countryId,
  value,
  onChange,
  isDisabled = false,
  placeholder = "Seleccione departamento...",
}) => {
  const isDarkMode = useDark();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // cache local de labels para preservar visualmente el seleccionado
  const labelCacheRef = useRef(new Map());
  const selectedLabelRef = useRef("");

  const customStyles = useMemo(
    () => ({
      control: (p, s) => ({
        ...p,
        minHeight: 38,
        backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
        borderColor: s.isFocused ? "#6c63ff" : isDarkMode ? "#444" : "#ced4da",
        boxShadow: s.isFocused ? "0 0 0 0.2rem rgba(108,99,255,.25)" : "none",
      }),
      menu: (p) => ({
        ...p,
        backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
        zIndex: 20000,
      }),
      menuPortal: (b) => ({ ...b, zIndex: 20000 }),
      singleValue: (p) => ({ ...p, color: isDarkMode ? "#fff" : "#000" }),
      option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected
          ? "#6c63ff"
          : s.isFocused
          ? isDarkMode
            ? "#2c2c3e"
            : "#f0f0f0"
          : isDarkMode
          ? "#1e1e2f"
          : "#fff",
        color: isDarkMode ? "#fff" : "#000",
        cursor: "pointer",
      }),
      placeholder: (p) => ({ ...p, color: isDarkMode ? "#aaa" : "#888" }),
      input: (p) => ({ ...p, color: isDarkMode ? "#fff" : "#000" }),
    }),
    [isDarkMode]
  );

  useEffect(() => {
    let mounted = true;

    // preserva el label actual antes de recargar
    if (value) {
      const known = options.find((o) => o.value === value)?.label;
      if (known) selectedLabelRef.current = known;
    }

    if (!countryId) {
      setOptions([]);
      return;
    }

    setLoading(true);
    (async () => {
      try {
        const { options: opts } = await getDepartmentOptions(countryId);
        if (!mounted) return;
        setOptions(opts || []);
        (opts || []).forEach((o) => {
          if (o?.value && o?.label) labelCacheRef.current.set(o.value, o.label);
        });
      } catch {
        if (mounted) setOptions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  // arma el "selected option" aun si el value no está (fallback sintético)
  const selected = useMemo(() => {
    if (!value) return null;
    const real = options.find((o) => o.value === value);
    if (real) return real;
    const label =
      labelCacheRef.current.get(value) ||
      selectedLabelRef.current ||
      "Cargando…";
    return { value, label, __synthetic: true };
  }, [options, value]);

  const disabled = isDisabled || loading || !countryId;

  return (
    <Select
      styles={customStyles}
      placeholder={placeholder}
      isClearable
      isDisabled={disabled}
      isLoading={loading}
      menuPortalTarget={document.body}
      options={options}
      value={selected}
      onChange={(opt) => {
        if (opt?.value && opt?.label) {
          labelCacheRef.current.set(opt.value, opt.label);
          selectedLabelRef.current = opt.label;
        }
        onChange?.(opt?.value || "");
      }}
    />
  );
};

export default SelectDepartment;
